import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  formatAutomationRunReason,
  RUN_STATUS_VARIANTS,
} from '@/lib/automations';
import type { AutomationRule, AutomationRun } from '@/api/generated/client';

interface RunHistoryDialogProps {
  rule: AutomationRule | null;
  runs: AutomationRun[];
  isLoading: boolean;
  error: unknown;
  onOpenChange: (open: boolean) => void;
}

export function RunHistoryDialog({
  rule,
  runs,
  isLoading,
  error,
  onOpenChange,
}: RunHistoryDialogProps) {
  return (
    <Dialog open={rule !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Run history</DialogTitle>
          <DialogDescription>
            Recent runs for &quot;{rule?.name}&quot; (newest first)
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[50dvh] md:max-h-[50vh] space-y-2 overflow-y-auto">
          {isLoading && <p className="text-sm text-muted-foreground">Loading runs…</p>}
          {!isLoading && Boolean(error) && (
            <p className="text-sm text-destructive">
              Couldn&apos;t load run history. Please try again.
            </p>
          )}
          {!isLoading && !error && runs.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No runs yet. This automation will run the next time its trigger fires.
            </p>
          )}
          {runs.map((run) => (
            <div
              key={run.id}
              className="flex items-start justify-between gap-3 rounded-md border p-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Badge variant={RUN_STATUS_VARIANTS[run.status] ?? 'outline'}>{run.status}</Badge>
                  {run.claimNumber && (
                    <span className="truncate text-sm font-medium">Claim {run.claimNumber}</span>
                  )}
                </div>
                {formatAutomationRunReason(run.reason) && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {formatAutomationRunReason(run.reason)}
                  </p>
                )}
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                {new Date(run.createdAt).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
